import {
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import PersonIcon from "@mui/icons-material/Person";
import LinkActivity from "./LinkActivity";
import { CampaignCount, CampaignOverviewType } from "models/interfaces";
import UserActionModal from "./UserActionModal";
import { useState } from "react";
import campaignStyles from "screens/Campaigns/Campaigns.styles";
import PerformanceChart from "./PerformanceChart";
import RetargetedCampaigns from "./RetargetedCampaigns";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";
import { getFormattedStatsCount } from "helpers/methods";
import { useAppSelector } from "utils/hooks";
import { selectAccount } from "redux/authSlice";

interface CustomProps {
  currentTab: string;
  setCurrentTab: Function;
  campaign: CampaignOverviewType;
  setCampaign: Function;
  failedMetrics: CampaignCount[];
  showRetarget?: boolean;
  failedMetricsNew?: number;
}
const Overview = (props: CustomProps) => {
  const theme = useTheme();
  const accountName = useAppSelector(selectAccount);
  const classes = campaignStyles(theme);
  const showRetarget = props.showRetarget ?? true;
  const [userActionModalOpen, setUserActionModalOpen] = useState<{
    open: boolean;
    for: string;
  }>({ open: false, for: "Open" });

  const getPercentage = (count: number, divideby: number) => {
    return ((count / (divideby === 0 ? 1 : divideby)) * 100).toFixed();
  };

  const getAdditionalMetrics = () => {
    const sent =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 2)
        ?.count || 0;
    const success =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 3)
        ?.count || 0;
    const failed =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 4)
        ?.count || 0;
    const unsubscribed =
      props.campaign?.campaignCounts.find((item) => item.metricId === 7)
        ?.count || 0;
    const hardBounce =
      props.failedMetrics.find((item) => item.metricId === 10)?.count || 0;
    const softBounce =
      props.failedMetrics.find((item) => item.metricId === 9)?.count || 0;
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">Failure rate</Typography>
                <CustomInfoTooltip
                  placement="top"
                  infoMessage={
                    "The number of emails that failed to be delivered due to bounces, invalid addresses, or other delivery issues."
                  }
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={classes.stackLine}
                  onClick={() =>
                    setUserActionModalOpen({ open: true, for: "Failed" })
                  }
                >
                  <PersonIcon fontSize="small" sx={{ color: "#B9BABB" }} />
                  <Typography variant="body2">
                    {getFormattedStatsCount(failed)}
                  </Typography>
                </Stack>
              </Stack>
              <Typography fontWeight={700}>
                {getPercentage(failed, sent)}%
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">Hard bounce</Typography>
                <CustomInfoTooltip
                  placement="top"
                  infoMessage={
                    "The number of hard bounces due to undeliverable email addresses."
                  }
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={classes.stackLine}
                  onClick={() =>
                    setUserActionModalOpen({ open: true, for: "Hard Bounce" })
                  }
                >
                  <PersonIcon fontSize="small" sx={{ color: "#B9BABB" }} />
                  <Typography variant="body2">
                    {getFormattedStatsCount(hardBounce)}
                  </Typography>
                </Stack>
              </Stack>
              <Typography fontWeight={700}>
                {getPercentage(hardBounce, sent)}%
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">Unsubscribe rate</Typography>
                <CustomInfoTooltip
                  placement="top"
                  infoMessage={
                    "The number of recipients who unsubscribed from your emails."
                  }
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={classes.stackLine}
                  onClick={() =>
                    setUserActionModalOpen({ open: true, for: "Unsubscribe" })
                  }
                >
                  <PersonIcon fontSize="small" sx={{ color: "#B9BABB" }} />
                  <Typography variant="body2">
                    {getFormattedStatsCount(unsubscribed)}
                  </Typography>
                </Stack>
              </Stack>
              <Typography fontWeight={700}>
                {getPercentage(unsubscribed, success)}%
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1">Soft bounce</Typography>
                <CustomInfoTooltip
                  placement="top"
                  infoMessage={
                    "The number of temporarily undeliverable emails due to full inboxes or server issues."
                  }
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={classes.stackLine}
                  onClick={() =>
                    setUserActionModalOpen({ open: true, for: "Soft Bounce" })
                  }
                >
                  <PersonIcon fontSize="small" sx={{ color: "#B9BABB" }} />
                  <Typography variant="body2">
                    {getFormattedStatsCount(softBounce)}
                  </Typography>
                </Stack>
              </Stack>
              <Typography fontWeight={700}>
                {getPercentage(softBounce, sent)}%
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
      </Grid>
    );
  };
  const getCounts = () => {
    const requested =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 1)
        ?.count || 0;
    const sent =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 2)
        ?.count || 0;
    const success =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 3)
        ?.count || 0;
    const open =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 5)
        ?.count || 0;
    const click =
      props.campaign?.campaignCounts?.find((item) => item.metricId === 6)
        ?.count || 0;
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Box p={2}>
          <Typography>{`Total emails delivered out of ${getFormattedStatsCount(
            requested
          )} requested`}</Typography>
          <Typography variant="h3">
            {getFormattedStatsCount(success)}
          </Typography>
          <Grid container columnSpacing={3} mt={1} rowSpacing={2}>
            <Grid item lg={4} xl={4} md={6} sm={12} xs={12}>
              <Stack direction="column">
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">Open rate</Typography>
                    <CustomInfoTooltip
                      placement="top"
                      infoMessage={
                        "The number of unique recipients who opened your email."
                      }
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={classes.stackLine}
                      onClick={() =>
                        setUserActionModalOpen({ open: true, for: "Open" })
                      }
                    >
                      <PersonIcon fontSize="small" sx={{ color: "#B9BABB" }} />
                      <Typography variant="body2">
                        {getFormattedStatsCount(open)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography variant="h5" color="primary">
                    {getPercentage(open, success)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={parseInt(getPercentage(open, success))}
                  sx={{
                    height: "8px",
                    mt: 1,
                    backgroundColor: "#47556940",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#91CD26",
                    },
                  }}
                />
              </Stack>
            </Grid>
            <Grid item lg={4} xl={4} md={6} sm={12} xs={12}>
              <Stack direction="column">
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">Click rate</Typography>
                    <CustomInfoTooltip
                      placement="top"
                      infoMessage={
                        "The number of recipients who clicked on at least one link in your email."
                      }
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      onClick={() =>
                        setUserActionModalOpen({ open: true, for: "Click" })
                      }
                      sx={classes.stackLine}
                    >
                      <PersonIcon fontSize="small" sx={{ color: "#B9BABB" }} />
                      <Typography variant="body2">
                        {getFormattedStatsCount(click)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography variant="h5" color="primary">
                    {getPercentage(click, success)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={parseInt(getPercentage(click, success))}
                  sx={{
                    height: "8px",
                    mt: 1,
                    backgroundColor: "#47556940",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#91CD26",
                    },
                  }}
                />
              </Stack>
            </Grid>
            <Grid item lg={4} xl={4} md={6} sm={12} xs={12}>
              <Stack direction="column">
                <Stack direction="row" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1">Click to open rate</Typography>
                    <CustomInfoTooltip
                      placement="top"
                      infoMessage={
                        "The percentage of email openers who clicked on a link."
                      }
                    />
                  </Stack>
                  <Typography variant="h5" color="primary">
                    {getPercentage(click, open)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={parseInt(getPercentage(click, open))}
                  sx={{
                    height: "8px",
                    mt: 1,
                    backgroundColor: "#47556940",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#91CD26",
                    },
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Divider flexItem sx={{ color: "#47556940/" }} />
        <Box p={2}>{getAdditionalMetrics()}</Box>
      </CenteredCardLayout>
    );
  };

  const getLinkActivity = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontSize="20px" fontWeight="600">
                Link Activity
              </Typography>
              <CustomInfoTooltip
                placement="top"
                infoMessage={
                  "Analyze which links were clicked to understand engagement."
                }
              />
            </Stack>
            <Button variant="text" onClick={() => props.setCurrentTab("link")}>
              View all
            </Button>
          </Stack>
          <LinkActivity maxRows={5} id={props.campaign?.campaign?.id} />
        </Box>
      </CenteredCardLayout>
    );
  };

  const getRetargetedCampaigns = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            p={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontSize="20px" fontWeight="600">
                Retargeted Campaigns
              </Typography>
              <CustomInfoTooltip
                placement="top"
                infoMessage={
                  "See follow-up campaigns sent to recipients based on their actions."
                }
              />
            </Stack>
            <Button
              variant="text"
              onClick={() => props.setCurrentTab("retargeted-campaigns")}
            >
              View all
            </Button>
          </Stack>
          <RetargetedCampaigns maxRows={5} id={props.campaign?.campaign?.id} />
        </Box>
      </CenteredCardLayout>
    );
  };

  const getChart = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Box p={2}>
          <PerformanceChart
            campaignId={props.campaign?.campaign?.id}
            campaignScheduleDate={props.campaign?.campaign?.scheduleTime}
            campaign={props.campaign}
          />
        </Box>
      </CenteredCardLayout>
    );
  };

  return (
    <Stack direction="column" spacing={2}>
      {getCounts()}
      {accountName !== "mwaa" && getChart()}
      {getLinkActivity()}
      {showRetarget &&
        props.campaign.campaign.haveChildren &&
        getRetargetedCampaigns()}
      <UserActionModal
        for={userActionModalOpen.for}
        open={userActionModalOpen.open}
        setOpen={setUserActionModalOpen}
        campaignId={props.campaign?.campaign?.id}
      />
    </Stack>
  );
};

export default Overview;
